import React, { useEffect } from 'react';
import { Flex, useDisclosure, useToast } from '@chakra-ui/react';
import { Contract, SignatureModal } from 'ts/common/components';
import ResponsiveButtonGroup from 'ts/common/components/ResponsiveButtonGroup';
import { ContractContext } from 'ts/common/contract/types';
import { useTranslate } from 'ts/common/hooks';
import { useClientBookingExperience } from 'client_react/booking/ClientBookingExperience';
import { ROUTE } from 'client_react/booking/common';
import BookingSessionHeading from 'client_react/booking/components/BookingSessionHeading';
import { isContractError } from 'client_react/common/hooks/useContract';
import { useContractResponsiveButtons } from 'client_react/contract/hooks/useContractResponsiveButtons';

interface ContractPageProps {
    isConfirmation?: boolean;
}

const ContractPage = ({ isConfirmation }: ContractPageProps) => {
    const {
        bookingSession,
        bookingSessionType,
        contract,
        heartbeatBookingSession: heartbeat,
        isPending,
        navigateWithSession,
        setSignatureData,
        submitPayment,
        updateContract,
        signContract
    } = useClientBookingExperience();
    const t = useTranslate('booking.contractPage');
    const { isOpen, onOpen: openSignatureModal, onClose } = useDisclosure();
    const brandName = bookingSession?.brandName ?? bookingSessionType?.brandName ?? '';
    const { fillableFieldData, setFillableFieldData, handleContractStart, getButtons } =
        useContractResponsiveButtons({
            t,
            onBack: () => navigateWithSession(ROUTE.CONTACT_INFORMATION),
            onSign: openSignatureModal,
            context: ContractContext.CLIENT_BOOKING_CONTRACT,
            heartbeat,
            contract: contract || undefined,
            contractUserEmail: contract?.contacts[0]?.email,
            isConfirmation
        });

    const errorToast = useToast({
        title: t('apiError'),
        status: 'error'
    });

    useEffect(() => {
        if (isPending) {
            return;
        }

        if (!bookingSession) {
            navigateWithSession(ROUTE.DATE_AND_TIME);
        } else if (!contract) {
            navigateWithSession(ROUTE.CONTACT_INFORMATION);
        }
    }, [bookingSession, contract, isPending, navigateWithSession]);

    const handleSign = async (signaturePaths: string, svgViewbox: string) => {
        const updateContractResponse = await updateContract({
            data: {
                id: contract?.publicId,
                fillableFieldData: fillableFieldData,
                type: 'contract'
            },
            search: {
                email: contract?.contacts[0].email ?? ''
            }
        });

        if (!updateContractResponse || isContractError(updateContractResponse)) {
            errorToast();
            return;
        }

        // Store signature data instead of submitting it immediately
        setSignatureData({
            signaturePaths,
            svgViewbox
        });

        if (bookingSession?.stripePrice === 0) {
            await submitPayment({ paymentAmountType: 'full' });

            // Submit signature since we're skipping the payment page
            const contractSignatureResponse = await signContract({
                data: {
                    email: updateContractResponse.contacts[0].email,
                    signaturePaths,
                    svgViewbox,
                    state: updateContractResponse.state,
                    type: 'contract-signature'
                }
            });

            if (!contractSignatureResponse || isContractError(contractSignatureResponse)) {
                errorToast();
                return;
            }

            navigateWithSession(ROUTE.CONFIRMATION);
        } else {
            navigateWithSession(ROUTE.PAYMENT);
        }
    };

    if (!bookingSession || !bookingSessionType || !contract) {
        return null;
    }

    return (
        <>
            {!isConfirmation && (
                <BookingSessionHeading
                    bookingSession={bookingSession}
                    bookingSessionType={bookingSessionType}
                />
            )}
            <Contract
                brandName={brandName}
                contractData={contract}
                contractUserEmail={contract?.contacts[0].email}
                heartbeat={heartbeat}
                openSignatureModal={openSignatureModal}
                onFillableFieldsChange={setFillableFieldData}
                onStart={handleContractStart}
                context={ContractContext.CLIENT_BOOKING_CONTRACT}
            />
            <Flex justifyContent="center" mt={8}>
                <ResponsiveButtonGroup buttons={getButtons()} />
            </Flex>
            <SignatureModal
                isOpen={isOpen}
                onClose={onClose}
                onAcceptAndSign={handleSign}
                context={ContractContext.CLIENT_BOOKING_CONTRACT}
            />
        </>
    );
};

export default ContractPage;
